import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import {
  TaxonomyDictionaryApiProvider,
  useTaxonomyDictionaryApi,
} from '../Services/TaxonomyDictionaryApiProvider';
import { formatDateTimeLocal } from '../utils/formatDate';
import useTablePagination from '../Hooks/useTablePagination';

function TaxonomyDictionaryHub({ canEdit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getTaxonomyDictionary, deleteTaxonomyDictionary } = useTaxonomyDictionaryApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [alertShown, setAlertShown] = useState(false);

  const tableTitle = 'Taxonomy Dictionary';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: false },
    { name: 'Title', selector: (row) => row.title, sortable: false },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: false },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at), sortable: false },
    { name: 'Updated', selector: (row) => formatDateTimeLocal(row.updated_at), sortable: false },
  ];

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, alertShown, navigate, location.pathname, showAlert]);

  const fetchDictionary = async (skip, limit) => {
    setLoading(true);
    try {
      const response = await getTaxonomyDictionary(skip, limit);
      setItems(response.items);
      setTotalItems(response.totalCount);
    } catch (err) {
      console.error('Error fetching dictionary items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDictionary(storedSkip(), storedLimit());
  }, []);

  const handleDelete = (selectedId) => {
    if (!selectedId) return;
    showConfirmation('Are you sure you want to delete this dictionary entry?', async () => {
      try {
        setLoading(true);
        await deleteTaxonomyDictionary(selectedId);
        fetchDictionary(storedSkip(), storedLimit());
        showAlert('success', 'Entry was successfully deleted!');
      } catch (err) {
        showAlert('fail', 'Failed to delete the entry');
      }
    });
  };

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/taxonomy-dictionary/${row.id}`);
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={items}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={initialPageSize}
                paginationTotalRows={totalItems}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                onChangePage={fetchDictionary}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/taxonomy-dictionary/new-dictionary`)
                  }
                >
                  Create New Dictionary
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TaxonomyDictionaryHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <TaxonomyDictionaryApiProvider>
        <TaxonomyDictionaryHub canEdit={canEdit} />
      </TaxonomyDictionaryApiProvider>
    </ConfirmationProvider>
  );
}

export default TaxonomyDictionaryHubWrapper;
